import type {
  MailFolder,
  MeResponse,
  MessageDetail,
  MessageListPage,
  MailSettings,
  OutgoingMessage,
  SignatureSettings,
  UploadedAttachment,
} from "./types.js";

/**
 * Eigen header die een cross-site formulier of <img> niet kan meesturen zonder
 * preflight; de server weigert elke schrijfactie zonder deze header.
 */
const CSRF_HEADER = "x-rw-csrf";

/** Elk verzoek loopt over dezelfde origin, dus paden en geen absolute URL's. */
const BASE = "/api";

/**
 * Fout met een tekst die rechtstreeks aan de gebruiker getoond mag worden. De
 * server formuleert zijn meldingen bewust generiek (geen "wachtwoord onjuist"),
 * dus nemen we ze letterlijk over; alleen als hij niets meegeeft vullen we zelf
 * iets in.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Elke mapwijziging antwoordt met de verse lijst, zodat de UI niet hoeft na te vragen. */
export interface FolderMutation {
  folders: MailFolder[];
}

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

/**
 * De AuthContext registreert hier hoe een verlopen sessie opgeruimd wordt. Zo
 * hoeft geen enkele aanroepplek zelf op 401 te controleren.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * Bij het opstarten is 401 het normale antwoord van /auth/me voor een bezoeker
   * die nog niet ingelogd is — dat mag geen "sessie verlopen" opleveren.
   */
  quietUnauthorized?: boolean;
}

function fallbackMessage(status: number): string {
  if (status === 401) return "Uw sessie is verlopen. Log opnieuw in.";
  if (status === 403) return "Deze actie is geweigerd. Ververs de pagina en probeer het opnieuw.";
  if (status === 404) return "Dit item bestaat niet meer.";
  if (status === 413) return "Het bestand is te groot.";
  if (status === 429) return "Te veel pogingen. Probeer het over een paar minuten opnieuw.";
  if (status >= 500) return "Er ging iets mis op de server. Probeer het later opnieuw.";
  return "Er ging iets mis. Probeer het opnieuw.";
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = "";

  // Een foutantwoord hoeft geen JSON te zijn (nginx, proxy, rate-limiter).
  try {
    const body: unknown = await response.json();
    if (body !== null && typeof body === "object") {
      const record = body as Record<string, unknown>;
      if (typeof record["error"] === "string") code = record["error"];
      if (typeof record["message"] === "string") message = record["message"];
    }
  } catch {
    /* geen bruikbare body; de terugvaltekst hieronder volstaat */
  }

  return new ApiError(response.status, code, message || fallbackMessage(response.status));
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  if (method !== "GET") headers[CSRF_HEADER] = "1";
  if (options.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      // Het sessiecookie is httpOnly; zonder dit stuurt fetch het niet mee.
      credentials: "include",
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    // Netwerkfouten mogen nooit als rauwe TypeError doorlekken naar de UI.
    throw new ApiError(0, "network", "Geen verbinding met de server. Probeer het opnieuw.");
  }

  if (response.status === 401 && options.quietUnauthorized !== true) onUnauthorized?.();

  if (!response.ok) throw await toApiError(response);

  if (response.status === 204) return undefined as T;
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(response.status, "bad_response", "Onverwacht antwoord van de server.");
  }
}

/** Mappaden bevatten punten en soms slashes; altijd door de encoder. */
function folderSegment(folder: string): string {
  return encodeURIComponent(folder);
}

/**
 * Pad naar één bijlage. De server serveert die als octet-stream met
 * Content-Disposition: attachment — nooit het Content-Type uit het bericht.
 */
export function attachmentUrl(folder: string, uid: number, partId: string): string {
  return `${BASE}/folders/${folderSegment(folder)}/messages/${uid}/attachments/${encodeURIComponent(partId)}`;
}

/** Bron voor het sandboxed iframe met de gesaneerde HTML van een bericht. */
export function bodyUrl(folder: string, uid: number, showImages: boolean): string {
  return `${BASE}/folders/${folderSegment(folder)}/messages/${uid}/body?images=${showImages ? "1" : "0"}`;
}

export const api = {
  me(quietUnauthorized = false): Promise<MeResponse> {
    return request<MeResponse>("/auth/me", { quietUnauthorized });
  },

  login(address: string, password: string, remember = false): Promise<MeResponse> {
    return request<MeResponse>("/auth/login", {
      method: "POST",
      body: { address, password, remember },
      // Een mislukte login is geen verlopen sessie; de loginpagina toont de
      // melding zelf.
      quietUnauthorized: true,
    });
  },

  logout(): Promise<void> {
    return request<void>("/auth/logout", { method: "POST", quietUnauthorized: true });
  },

  folders(): Promise<MailFolder[]> {
    return request<MailFolder[]>("/folders");
  },

  // ── Mappenbeheer ──────────────────────────────────────────────────────────

  /*
   * Elke wijziging geeft de verse mappenlijst mee terug, dus na deze aanroepen
   * hoeft er geen losse api.folders() achteraan. Dat scheelt een verzoek en,
   * belangrijker, het voorkomt dat de poller er met een oud plaatje tussen komt.
   */

  /** `parent: null` = op het hoogste niveau; de server bouwt het pad. */
  createFolder(name: string, parent: string | null): Promise<FolderMutation & { path: string }> {
    return request("/folders", { method: "POST", body: { name, parent } });
  },

  /**
   * Hernoemen en verplaatsen zijn dezelfde handeling: IMAP RENAME neemt de hele
   * subboom mee. `parent: null` hangt de map op het hoogste niveau.
   */
  renameFolder(
    path: string,
    changes: { name?: string; parent?: string | null },
  ): Promise<FolderMutation & { path: string; previousPath: string }> {
    return request(`/folders/${folderSegment(path)}`, { method: "PATCH", body: changes });
  },

  /**
   * `recursive` is nodig zodra de map submappen heeft, `confirm` zodra er post
   * in zit. De server weigert zonder; dat is de bevestigingsstap.
   */
  deleteFolder(
    path: string,
    options: { recursive?: boolean; confirm?: boolean } = {},
  ): Promise<FolderMutation & { removed: string[] }> {
    const query = new URLSearchParams({
      recursive: options.recursive === true ? "1" : "0",
      confirm: options.confirm === true ? "1" : "0",
    });
    return request(`/folders/${folderSegment(path)}?${query.toString()}`, { method: "DELETE" });
  },

  messages(folder: string, offset: number, limit: number): Promise<MessageListPage> {
    const query = new URLSearchParams({ offset: String(offset), limit: String(limit) });
    return request<MessageListPage>(`/folders/${folderSegment(folder)}/messages?${query.toString()}`);
  },

  message(folder: string, uid: number): Promise<MessageDetail> {
    return request<MessageDetail>(`/folders/${folderSegment(folder)}/messages/${uid}`);
  },

  setFlags(
    folder: string,
    uid: number,
    changes: { seen?: boolean; flagged?: boolean },
  ): Promise<void> {
    return request<void>(`/folders/${folderSegment(folder)}/messages/${uid}/flags`, {
      method: "PATCH",
      body: changes,
    });
  },

  /**
   * Dezelfde vlaggen over een hele selectie. Bewust één verzoek: de server mag
   * per sessie maar twee IMAP-verbindingen tegelijk gebruiken, dus vijftig
   * losse aanroepen lopen gegarandeerd vast.
   */
  setFlagsBulk(
    folder: string,
    uids: number[],
    changes: { seen?: boolean; flagged?: boolean },
  ): Promise<void> {
    return request<void>(`/folders/${folderSegment(folder)}/flags`, {
      method: "POST",
      body: { uids, ...changes },
    });
  },

  // ── Ordenen ───────────────────────────────────────────────────────────────

  /**
   * Verwijderen gaat standaard naar de Prullenbak. Pas een tweede keer (of
   * verwijderen ván de Prullenbak) is definitief — de server bepaalt dat, niet
   * de client.
   */
  remove(folder: string, uids: number[], permanent = false): Promise<{ mode: string; count: number }> {
    return request(`/folders/${folderSegment(folder)}/delete`, {
      method: "POST",
      body: { uids, permanent },
    });
  },

  move(folder: string, uids: number[], target: string): Promise<{ moved: number; target: string }> {
    return request(`/folders/${folderSegment(folder)}/move`, {
      method: "POST",
      body: { uids, target },
    });
  },

  search(
    folder: string,
    q: string,
    scope: "all" | "from" | "subject" | "to",
    offset: number,
    limit: number,
  ): Promise<MessageListPage> {
    const query = new URLSearchParams({ q, scope, offset: String(offset), limit: String(limit) });
    return request<MessageListPage>(`/folders/${folderSegment(folder)}/search?${query.toString()}`);
  },

  // ── Opstellen ─────────────────────────────────────────────────────────────

  /**
   * Bijlage uploaden. Gaat als multipart naar de server, die hem streamend naar
   * schijf schrijft; pas bij verzenden wordt hij in de MIME opgenomen.
   */
  async uploadAttachment(file: File): Promise<UploadedAttachment> {
    const form = new FormData();
    form.append("file", file, file.name);
    const response = await fetch(`${BASE}/compose/attachments`, {
      method: "POST",
      credentials: "same-origin",
      // Bewust géén Content-Type zetten: de browser moet zelf de multipart
      // boundary bepalen.
      headers: { "x-rw-csrf": "1" },
      body: form,
    });
    if (response.status === 401) onUnauthorized?.();
    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as UploadedAttachment;
  },

  discardAttachment(id: string): Promise<void> {
    return request<void>(`/compose/attachments/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  send(draft: OutgoingMessage): Promise<{ messageId: string; savedTo: string | null }> {
    return request("/compose/send", { method: "POST", body: draft });
  },

  saveDraft(draft: OutgoingMessage): Promise<{ folder: string; uid: number | null }> {
    return request("/compose/draft", { method: "POST", body: draft });
  },

  // ── Meldingen ─────────────────────────────────────────────────────────────

  pushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> {
    return request("/push/vapid");
  },

  pushSubscribe(subscription: PushSubscriptionJSON): Promise<void> {
    return request<void>("/push/subscribe", { method: "POST", body: subscription });
  },

  pushUnsubscribe(endpoint: string): Promise<void> {
    return request<void>("/push/unsubscribe", { method: "POST", body: { endpoint } });
  },

  pushStatus(): Promise<{ enabled: boolean; devices: number }> {
    return request("/push/status");
  },

  // ── Instellingen ──────────────────────────────────────────────────────────

  mailSettings(): Promise<MailSettings> {
    return request<MailSettings>("/profile/settings");
  },

  /**
   * Vraagt een eenmalig kaartje voor de profieldownload. In twee stappen omdat
   * een download een gewone navigatie is: die stuurt geen CSRF-header mee, en
   * een verlopen sessie zou anders als foutmelding in de map Downloads belanden.
   */
  profileTicket(withPassword: boolean): Promise<{ ticket: string; url: string; expiresInMs: number }> {
    return request("/profile/ticket", { method: "POST", body: { withPassword } });
  },

  /** Wijzigt het mailwachtwoord; geldt daarna overal (één wachtwoordbron). */
  changePassword(current: string, next: string): Promise<{ changed: boolean; otherSessionsEnded: number }> {
    return request("/settings/password", { method: "POST", body: { current, next } });
  },

  signature(): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature");
  },

  /** Geeft de GESANEERDE versie terug, dus toon altijd wat hier uitkomt. */
  saveSignature(input: Partial<SignatureSettings>): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature", { method: "PUT", body: input });
  },

  /**
   * Logo voor in de handtekening. De server controleert het formaat aan de
   * eerste bytes en stuurt het bij verzenden als inline bijlage mee, zodat de
   * ontvanger het ziet zonder iets van buitenaf op te halen.
   */
  async uploadSignatureLogo(file: File): Promise<SignatureSettings> {
    const form = new FormData();
    form.append("file", file, file.name);
    const response = await fetch(`${BASE}/settings/signature/logo`, {
      method: "POST",
      credentials: "same-origin",
      // Geen Content-Type zetten: de browser bepaalt zelf de multipart boundary.
      headers: { [CSRF_HEADER]: "1" },
      body: form,
    });
    if (response.status === 401) onUnauthorized?.();
    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as SignatureSettings;
  },

  removeSignatureLogo(): Promise<SignatureSettings> {
    return request<SignatureSettings>("/settings/signature/logo", { method: "DELETE" });
  },
};

/**
 * Bron voor het logo van de ingelogde gebruiker. `updatedAt` gaat als
 * cache-buster mee, want de server mag het plaatje vijf minuten cachen en een
 * net geüpload logo moet meteen zichtbaar zijn.
 */
export function signatureLogoUrl(updatedAt: number): string {
  return `${BASE}/settings/signature/logo?v=${updatedAt}`;
}
